// Standard library imports
import React, { useContext, useEffect, useState } from 'react';

// External library imports
import moment from 'moment-timezone';
import _ from 'lodash';

// Internal module imports
import Typography from '../../../components/Typography/Typography';
import { momentTimeFormater, timeOnly } from '../../../utils/dateHelper';
import { DownloadAsExcel } from '../../../utils/downloadAsExcel';
import TabsComponent from '../../../components/Tabs/Tabs';
import DatePicker from '../../../components/Inputs/DatePicker/DatePicker';
import DownloadButton from '../../../components/Buttons/DownloadButton';
import { START_LOADER, STOP_LOADER } from '../../../constants';
import { LoaderContext } from '../../../context/LoaderContext';
import { SCService } from '../../../services/SCService';
import { Capitalize } from '../../../utils/stringHelper';
import { DropdownComponent } from '../../../components/Inputs/Input';
import { usToEuCurrencyFormat } from '../../../utils/currencyHelper';
import EChart from '../../../components/Echarts/Echarts';

// Css imports
import classes from '../../../styles/Overview.module.css';
import { toCET } from '../../../utils/timeHelper';

const today = new Date();
const yesterday = new Date(today);
yesterday.setDate(yesterday.getDate() - 1);

const Overview = ({ selectedInstance, updateQueryParams, queryParamsData }) => {
    const { dispatch: loaderDispatch } = useContext(LoaderContext);
    const startLoader = (payload) => loaderDispatch({ type: START_LOADER, payload });
    const stopLoader = (payload) => loaderDispatch({ type: STOP_LOADER, payload });

    const [lastStatus, setLastStatus] = useState('--');
    const [date, setDate] = useState(queryParamsData.date ? queryParamsData.date : today);
    const [graphData, setGraphData] = useState([]);
    const [consumptionData, setConsumptionData] = useState([]);
    const [socData, setSocData] = useState([]);

    const [selectedIndex] = useState(queryParamsData.subTab ? queryParamsData.subTab : 0);
    const [connectors, setConnectors] = useState([]);
    const [selectedConnector, setSelectedConnector] = useState();
    const [lastSession, setLastSession] = useState({});

    useEffect(() => {
        if (selectedInstance?.meta?.connectorDetails) {
            const formattedConnectorDetails = selectedInstance?.meta?.connectorDetails.map((connector) => ({
                label: connector.connectorId,
                value: connector.connectorId,
            }));

            setConnectors(formattedConnectorDetails);
            const matchedConnector = formattedConnectorDetails.find((item) => item.value === queryParamsData.connector);
            setSelectedConnector(matchedConnector || formattedConnectorDetails[0]);
        }
    }, [selectedInstance?.meta?.connectors, selectedInstance?._id]);

    useEffect(() => {
        updateQueryParams('date', date);
    }, [date]);

    useEffect(() => {
        if (selectedInstance?._id && selectedConnector?.value) {
            let filteredParams = {
                evseId: selectedInstance?.meta?.evseId,
                connectorId: selectedConnector.value,
                startDate: momentTimeFormater(date).startOf('day').toDate(),
                endDate: momentTimeFormater(date).endOf('day').toDate(),
            };
            SCService.Overview(
                filteredParams,
                () => startLoader('getInstanceOverview'),
                handleGetDeviceDataSuccess,
                handleError,
                () => stopLoader('getInstanceOverview')
            );
        }
    }, [date, selectedConnector?.value]);

    useEffect(() => {
        if (selectedInstance?._id && selectedConnector?.value) {
            SCService.LastReported(
                {
                    evseId: selectedInstance?.meta?.evseId,
                    connectorId: selectedConnector.value,
                },
                () => startLoader('getOverviewGraphLastReported'),
                handleGetLastReportedSuccess,
                handleError,
                () => stopLoader('getOverviewGraphLastReported')
            );
        }
    }, [selectedConnector?.value]);

    useEffect(() => {
        getLastSessionDetails();
    }, [selectedInstance, selectedConnector?.value]);

    const getLastSessionDetails = () => {
        if (selectedInstance?._id && selectedConnector?.value) {
            SCService.GetLastSession(
                {
                    evseId: selectedInstance?.meta?.evseId,
                    connectorId: selectedConnector.value,
                },
                () => startLoader('GetLastSession'),
                handleGetLastSessionSuccess,
                handleError,
                () => stopLoader('GetLastSession')
            );
        }
    };

    const handleGetLastSessionSuccess = ({ data }) => {
        setLastSession(data?.data);
    };

    const handleGetDeviceDataSuccess = ({ data }) => {
        setGraphData(data.data.powerData);
        setConsumptionData(data.data.consumptionData || [{ data: [] }]);
        setSocData(data.data.socData || [{ data: [] }]);
    };
    const handleGetLastReportedSuccess = ({ data }) => {
        setLastStatus(data?.data?.lastReported);
    };

    const handleExport = (type) => {
        const powerData = graphData[0]?.data || [];
        const consumptionDataPoints = consumptionData[0]?.data || [];
        const socDataPoints = socData[0]?.data || [];

        const mergedMap = new Map();

        powerData.forEach((item) => {
            mergedMap.set(toCET(_.get(item, [0])), { date: toCET(_.get(item, [0])), power: _.get(item, [1]) });
        });

        consumptionDataPoints.forEach((item) => {
            const existing = mergedMap.get(toCET(_.get(item, [0]))) || { date: toCET(_.get(item, [0])) };
            existing.consumption = _.get(item, [1]);
            mergedMap.set(toCET(_.get(item, [0])), existing);
        });

        socDataPoints.forEach((item) => {
            const existing = mergedMap.get(toCET(_.get(item, [0]))) || { date: toCET(_.get(item, [0])) };
            existing.soc = _.get(item, [1]);
            mergedMap.set(toCET(_.get(item, [0])), existing);
        });

        const downloadData = Array.from(mergedMap.values());

        const headers = ['Date & Time', 'Power (kW)', 'Consumption (kWh)', 'SoC (%)'];

        if (downloadData.length) {
            DownloadAsExcel(downloadData, `${selectedInstance?.name} Smart Charging Data ${moment(date).format('DD-MM-YYYY')}`, headers);
        }
    };

    const handleError = (err) => console.log(err);

    const tabs = [
        {
            name: 'SoC (%)',
            component: (
                <div className={classes.ChartContainer}>
                    <div className={classes.Header}>
                        <div>
                            <Typography content={'Last Session Details:'} />
                            <div>
                                <Typography subtext content={'Session ID: ' + (lastSession?.session_id || ' --')} />
                                <Typography subtext content={`Tag ID / Name: ${lastSession?.cdr_token?.uid ? lastSession?.cdr_token?.uid : '--'} / ${lastSession?.tagName ? lastSession?.tagName : '--'}`} />
                            </div>
                        </div>
                        <div className={classes.Actions}>
                            <div className={classes.FieldControl2}>
                                <DropdownComponent defaultValue={selectedConnector || connectors[0]} options={connectors} onChange={(e) => setSelectedConnector(e)} />
                            </div>
                            <DatePicker date={date} setDate={setDate} />
                            <DownloadButton size="meduim" onClick={() => handleExport('soc')} />
                        </div>
                    </div>
                    <EChart series={socData} type="line" xFormatter={timeOnly} yFormatter={(v) => usToEuCurrencyFormat(parseFloat(v).toFixed(2))} yTooltipSuffix={['%']} />
                </div>
            ),
        },
        {
            name: 'Power (kW)',
            component: (
                <div className={classes.ChartContainer}>
                    <div className={classes.Header}>
                        <div>
                            <Typography content={'Last Session Details:'} />
                            <div>
                                <Typography subtext content={'Session ID: ' + (lastSession?.session_id || ' --')} />
                                <Typography subtext content={'Tag ID: ' + (lastSession?.cdr_token?.uid || ' --')} />
                            </div>
                        </div>
                        <div className={classes.Actions}>
                            <DropdownComponent
                                defaultValue={selectedConnector || connectors[0]}
                                options={connectors}
                                onChange={(e) => {
                                    setSelectedConnector(e);
                                    updateQueryParams('connector', e.value);
                                }}
                            />
                            <DatePicker date={date} setDate={setDate} />
                            <DownloadButton size="meduim" onClick={() => handleExport('power')} />
                        </div>
                    </div>
                    <EChart series={graphData} type="line" xFormatter={timeOnly} yFormatter={(v) => usToEuCurrencyFormat(parseFloat(v).toFixed(2))} yTooltipSuffix={['kW']} />
                </div>
            ),
        },
        {
            name: 'Consumption (kWh)',
            component: (
                <div className={classes.ChartContainer}>
                    <div className={classes.Header}>
                        <div>
                            <Typography content={'Last Session Details:'} />
                            <div>
                                <Typography subtext content={'Session ID: ' + (lastSession?.session_id || ' --')} />
                                <Typography subtext content={'Tag ID: ' + (lastSession?.cdr_token?.uid || ' --')} />
                            </div>
                        </div>
                        <div className={classes.Actions}>
                            <div className={classes.FieldControl2}>
                                <DropdownComponent defaultValue={selectedConnector || connectors[0]} options={connectors} onChange={(e) => setSelectedConnector(e)} />
                            </div>
                            <DatePicker date={date} setDate={setDate} />
                            <DownloadButton size="meduim" onClick={() => handleExport('consumption')} />
                        </div>
                    </div>
                    <EChart
                        series={consumptionData}
                        type="line"
                        lineType={'area'}
                        xFormatter={timeOnly}
                        yFormatter={(v) => usToEuCurrencyFormat(parseFloat(v).toFixed(2))}
                        yTooltipSuffix={['kWh']}
                    />
                </div>
            ),
        },
    ];

    return (
        <div className={classes.Overview}>
            <div className={classes.Header}>
                <div>
                    <Typography content={selectedInstance?.name || '--'} />
                    <div>
                        <Typography subtext content={'Last Reported: ' + (lastStatus || ' --')} />
                    </div>
                </div>
            </div>
            <div className={classes.OverviewBorder}></div>
            <div className={classes.OverviewContent}>
                <TabsComponent tabs={tabs} selectedIndex={selectedIndex} onChangeTab={(index) => updateQueryParams('subTab', index)} />
            </div>
        </div>
    );
};

export default Overview;
